/**
 * Free-shipping / free-gift thresholds, in the money the buyer is reading.
 *
 * The merchant types one number into the theme settings — "100" — and it means
 * 100 of the SHOP's currency, because that is what the Shopify discount behind
 * it compares against. Liquid then subtracts it from `cart.total_price`, which
 * Shopify has already converted into the BUYER's presentment currency, and
 * formats the difference with the money filter. Two currencies, one
 * subtraction, and the result wears the buyer's symbol.
 *
 * On this shop, settling in USD and presenting in AUD, the bar told an
 * Australian shopper "Spend $100 More For Free Shipping" when the discount
 * wanted 100 USD — about A$152. Worse than the number being off: the same
 * mixed comparison drives the "You've earned Free Shipping" branch, so a
 * A$105 cart was congratulated and then charged shipping at checkout. The page
 * promised what the cart would not honour, which is the one direction of error
 * that must never ship.
 *
 * Liquid cannot fix it: there is no presentment rate in Liquid. The rate is
 * only available to the browser, as Shopify.currency.rate, so the correction
 * has to happen here.
 *
 * DOES NOTHING when the buyer is in the shop's own currency (rate 1), which is
 * the common case — a shopper who never sees a conversion sees no change in
 * behaviour, and the server-rendered markup stands exactly as it did.
 *
 * Re-runs after every cart change: the drawer re-renders through Shopify's
 * Section Rendering API, so each update paints fresh Liquid with the same flaw.
 */
(function () {
  'use strict';

  function rate() {
    var c = window.Shopify && window.Shopify.currency;
    var r = c ? parseFloat(c.rate) : NaN;
    return isFinite(r) && r > 0 ? r : 1;
  }

  /**
   * Money in the buyer's currency.
   *
   * Prefers the theme's own formatter so the correction is punctuated like
   * every other price on the page; falls back to Intl, then to a plain figure.
   */
  function money(cents) {
    var amount = cents / 100;
    try {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        return window.Shopify.formatMoney(Math.round(cents));
      }
    } catch (e) { /* fall through */ }
    try {
      var active = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency: active,
      }).format(amount);
    } catch (e2) {
      return String(Math.round(amount * 100) / 100);
    }
  }

  /**
   * Put the corrected figure into the merchant's sentence.
   *
   * The setting is a TEMPLATE — "Spend [missingAmount] More For Free Shipping"
   * — so the placeholder is the normal path and there is no number to find.
   * The regex is only for a merchant who typed the amount into the sentence
   * instead, and it is deliberately narrow about what can sit in front of the
   * figure: an "any non-space" prefix swallowed the tail of the preceding word
   * and turned "Only 100 kr left" into "Okr 152 kr left".
   */
  function replaceAmount(text, formatted) {
    var s = String(text || '');
    if (s.indexOf('[missingAmount]') !== -1) return s.split('[missingAmount]').join(formatted);
    // Anchored to a word boundary so the token cannot start mid-word, and
    // replaced through a function because a formatted amount contains '$',
    // which a string replacement would read as a capture reference.
    return s.replace(
      /(^|\s)((?:[A-Z]{3}\s*)?[$\u20ac\u00a3\u00a5\u20a9\u20aa]?\d[\d.,]*)/,
      function (_m, lead) { return lead + formatted; },
    );
  }

  function correct(root) {
    var r = rate();
    if (r === 1) return; // buyer is in the shop's currency — nothing to convert

    var nodes = (root || document).querySelectorAll('[data-threshold-shop-cents]');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      var shopCents = parseFloat(el.getAttribute('data-threshold-shop-cents'));
      var cartCents = parseFloat(el.getAttribute('data-threshold-cart-cents'));
      if (!isFinite(shopCents) || shopCents <= 0 || !isFinite(cartCents)) continue;

      // The threshold in the same money as the cart total Shopify handed us.
      var goalCents = shopCents * r;
      var earned = cartCents >= goalCents;
      var awayTpl = el.getAttribute('data-threshold-away-text') || '';
      var earnTpl = el.getAttribute('data-threshold-earn-text') || '';

      // Liquid decided earned/away with the un-converted number, so it can be
      // wrong in either direction. Rewriting from the corrected comparison
      // fixes both — including the congratulation that was never earned.
      var next = earned ? earnTpl : (awayTpl ? replaceAmount(awayTpl, money(goalCents - cartCents)) : '');
      // Compared before writing. Assigning textContent replaces the text node
      // even when the string is identical, and that is a childList mutation
      // inside the very subtree this file observes — see the guard on run().
      if (next && el.textContent.trim() !== next) el.textContent = next;

      // The bar's fill comes from the same figure and overstates progress by
      // the same factor, so it is corrected from the same source.
      var bar = el.parentNode && el.parentNode.querySelector('progress');
      var max = String(Math.round(goalCents));
      if (bar && bar.getAttribute('max') !== max) bar.setAttribute('max', max);
    }
  }

  var observer = null;
  var host = null;
  var applying = false;

  function watch() {
    if (!observer || !host) return;
    try { observer.observe(host, { childList: true, subtree: true }); } catch (e) { /* ignore */ }
  }

  /**
   * Correct the page, without the correction waking the watcher.
   *
   * The message this file rewrites lives INSIDE the subtree it watches, so a
   * naive observer re-fires on its own write and calls itself forever. That is
   * not theoretical: it froze the renderer on the first live page load. The
   * detach-write-reattach pair is the fix; the re-entrancy flag and the
   * write-only-if-different checks above are the belt to its braces, because a
   * cart that can hang is worse than a threshold that reads wrong.
   */
  function run() {
    if (applying) return;
    applying = true;
    if (observer) { try { observer.disconnect(); } catch (e) { /* ignore */ } }
    try { correct(document); } catch (e) { /* never break the cart */ }
    applying = false;
    watch();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  // The drawer replaces its own markup on every cart change, so watch for the
  // fresh copy rather than correcting once and trusting it to survive.
  try {
    host = document.querySelector('cart-drawer') || document.body;
    if (host && window.MutationObserver) {
      observer = new MutationObserver(function () { run(); });
      watch();
    }
  } catch (e) { /* observation is a nicety, not a requirement */ }
})();
